import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  EditIcon,
  EyeIconSmall,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../../../components/button/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/popover/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import {
  ConversionEventItem,
  EventTypeBackend,
  TransactionSignatureFields,
} from '../../../../../types/events.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { formatDisplayValue } from '../../../../../utils/format-display-value.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { BlockedTooltipAction } from '../blocked-action-tooltip';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { EventSignatureStatusTableCell, Td } from './table-td';

export const Conversion: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);
  const { items, totalPages, isLoading } = EventHooks.useTransactions<ConversionEventItem>({
    eventId: props.id,
    eventType: EventTypeBackend.CONVERSION,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  if (props.type !== EventTypeBackend.CONVERSION) return <></>;

  const { eventId, onDelete, onEdit, onView, inCurrentRound, transactionView } = props;

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.CONVERSION}
        transactionView={transactionView}
        {...tableStateProps}
      >
        {items.map(
          (
            {
              date,
              fromShareClass,
              sharesCount,
              stakeholder,
              toShareClass,
              id: transactionId,
              signatureSignedCount,
              signatureStatus,
              signatureTotalCount,
            }: ConversionEventItem & TransactionSignatureFields,
            index,
          ) => (
            <tr key={`${index}_${stakeholder}`}>
              <Td value={format(date, 'dd/MM/yyyy')} />
              <Td
                value={<Tooltip content={stakeholder.fullName}>{stakeholder.fullName}</Tooltip>}
              />
              <Td value={fromShareClass?.name} />
              <Td value={toShareClass?.name} />
              <Td value={sharesCount.toLocaleString('en-US')} />
              <Td
                value={formatDisplayValue(sharesCount, {
                  multiply: fromShareClass.conversionRatio,
                })}
              />
              {transactionView && (
                <EventSignatureStatusTableCell
                  signatureSignedCount={signatureSignedCount}
                  signatureStatus={signatureStatus}
                  signatureTotalCount={signatureTotalCount}
                />
              )}
              {!transactionView && (
                <>
                  <Td
                    value={
                      <EventTransactionFilesCell
                        date={date}
                        filesLinks={props.filesLinks ?? []}
                        onDocumentClick={handleDocumentClick}
                        onOpenFilesModal={handleOpenFilesModalForRow}
                        parentEvent={props}
                        stakeholderName={stakeholder.fullName}
                      />
                    }
                  />
                  <Td
                    className="w-12"
                    value={
                      <Popover>
                        <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
                          <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                            <ThreeDotsIcon />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                          <PopoverClose asChild>
                            <div
                              className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => onView(eventId, transactionId)}
                            >
                              <EyeIconSmall />
                              <span className="text-sm font-normal text-gray-700">
                                <AppFormattedMessage id="VIEW_DETAILS" />
                              </span>
                            </div>
                          </PopoverClose>
                          <BlockedTooltipAction allowed={inCurrentRound}>
                            <PopoverClose asChild>
                              <Button
                                className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                                data-permission-required
                                disabled={!inCurrentRound}
                                disabledStyle={ButtonDisableStyle.NONE}
                                onClick={() => onEdit(eventId, transactionId)}
                                styleType="NONE"
                              >
                                <EditIcon iconColor={!inCurrentRound ? '#98A2B3' : undefined} />
                                <span
                                  className={twMerge(
                                    'text-sm font-normal text-gray-700',
                                    !inCurrentRound && 'text-gray-400',
                                  )}
                                >
                                  <AppFormattedMessage id="EDIT_DETAILS" />
                                </span>
                              </Button>
                            </PopoverClose>
                          </BlockedTooltipAction>
                          {(totalPages !== 1 || items.length > 1) && (
                            <BlockedTooltipAction allowed={inCurrentRound}>
                              <PopoverClose asChild>
                                <Button
                                  className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                                  data-permission-required
                                  disabled={!inCurrentRound}
                                  disabledStyle={ButtonDisableStyle.NONE}
                                  onClick={() => onDelete(props, transactionId)}
                                  styleType="NONE"
                                >
                                  <TrashBinIcon
                                    iconColor={inCurrentRound ? '#F04438' : '#98A2B3'}
                                  />
                                  <span
                                    className={twMerge(
                                      'text-sm font-normal text-fireside-500',
                                      !inCurrentRound && 'text-gray-400',
                                    )}
                                  >
                                    <AppFormattedMessage id="DELETE_TRANSACTION" />
                                  </span>
                                </Button>
                              </PopoverClose>
                            </BlockedTooltipAction>
                          )}
                        </PopoverContent>
                      </Popover>
                    }
                  />
                </>
              )}
            </tr>
          ),
        )}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
